import type { KkmHandshakeResult, KkmKktInfo } from '@/shared/api';

import {
  type GetOrderFiscalOperationalStatusQuery,
  OrderFiscalWorkstationBinding,
  type ShiftFieldsFragment,
  ShiftStatus,
} from '@shared/api/graphql/gql/graphql';
import { Temporal, clock, parseInstant, inTimezone, formatTime as formatLocalTime } from '@shared/lib/date-time';

import { parseKkmDateTime } from '../lib/kkm-fiscal-document-parser';

export type FiscalShiftAction = 'close' | 'continue_closing' | 'continue_opening' | 'open' | 'reopen' | null;

/** Подпись кнопки действия со сменой — единая для чипа, карточки и формы оплаты. */
export const fiscalShiftActionLabel: Record<Exclude<FiscalShiftAction, null>, string> = {
  close: 'Закрыть смену',
  continue_closing: 'Продолжить закрытие',
  continue_opening: 'Продолжить открытие',
  open: 'Открыть смену',
  reopen: 'Продолжить работу',
};
export type FiscalShiftStatus =
  | 'bridge_missing'
  | 'cashier_missing'
  | 'device_mismatch'
  | 'fn_expired'
  | 'fn_memory_overflow'
  | 'kkt_not_ready'
  | 'kkt_shift_orphan'
  | 'ofd_error'
  | 'paper_over'
  | 'paused'
  | 'shift_expired'
  | 'shift_expiring'
  | 'shift_kkt_desync'
  | 'shift_closing'
  | 'shift_opening'
  | 'shift_open'
  | 'shift_unknown'
  | 'shift_closed'
  | 'unavailable'
  | 'workstation_not_bound';

/**
 * Домен статуса: `'shift'` — про кассовую смену, `'kkt'` — про само устройство.
 * Блокер расчёта берёт только `'shift'`, карточка состояния кассы — только `'kkt'`.
 * `Record` обязателен: новый статус без домена не пройдёт type-check.
 */
export const fiscalShiftStatusDomain: Record<FiscalShiftStatus, 'kkt' | 'shift'> = {
  bridge_missing: 'kkt',
  cashier_missing: 'kkt',
  device_mismatch: 'kkt',
  fn_expired: 'kkt',
  fn_memory_overflow: 'kkt',
  kkt_not_ready: 'kkt',
  // Смена на кассе живёт, в Морсе её нет — проблема смены, а не железа.
  kkt_shift_orphan: 'shift',
  ofd_error: 'kkt',
  paper_over: 'kkt',
  paused: 'kkt',
  shift_closed: 'shift',
  shift_closing: 'shift',
  shift_expired: 'shift',
  shift_expiring: 'shift',
  shift_kkt_desync: 'shift',
  shift_open: 'shift',
  shift_opening: 'shift',
  shift_unknown: 'shift',
  unavailable: 'kkt',
  workstation_not_bound: 'kkt',
};

/** Статус описывает состояние кассы (железа), а не смены. */
export function isKktConcern(status: FiscalShiftStatus): boolean {
  return fiscalShiftStatusDomain[status] === 'kkt';
}

export type FiscalShiftTone = 'danger' | 'neutral' | 'success' | 'warning';

export interface FiscalShiftState {
  action: FiscalShiftAction;
  blocking: boolean;
  description: string;
  /** Касса включена, но её состояние ещё не опрашивали. Не отказ кассы и не повод блокировать оплату. */
  kktStatePending: boolean;
  status: FiscalShiftStatus;
  title: string;
  tone: FiscalShiftTone;
}

type FiscalOperationalStatus = GetOrderFiscalOperationalStatusQuery['orderFiscalOperationalStatus'];

export function fiscalShiftState(
  handshake: KkmHandshakeResult | null,
  operationalStatus: FiscalOperationalStatus,
  shift: ShiftFieldsFragment | null,
  now: Temporal.Instant = clock.now(),
  /** Зона клуба: время смены кассир читает по часам локации, а не по часам своего компьютера. */
  zone?: string,
): FiscalShiftState | null {
  if (!operationalStatus.shifts_enabled) {
    return null;
  }

  const resolved = resolveShiftState(handshake, operationalStatus, shift, now, zone);

  // Серверное состояние смены показываем как есть: «ещё не проверили» — не то же самое, что «касса не отвечает».
  return operationalStatus.fiscalization_enabled && handshake === null
    ? { ...resolved, kktStatePending: true }
    : resolved;
}

function resolveShiftState(
  handshake: KkmHandshakeResult | null,
  operationalStatus: FiscalOperationalStatus,
  shift: ShiftFieldsFragment | null,
  now: Temporal.Instant,
  zone: string | undefined,
): FiscalShiftState {
  // Ловится не только мастер подключения: смену на кассе могут открыть с пульта ККТ или сторонним ПО.
  if (shift === null && isKktSessionOpen(operationalStatus, handshake)) {
    return state(
      'kkt_shift_orphan',
      'danger',
      'На кассе открыта смена, в Морсе — нет',
      'Откройте смену в Морсе. Смену на кассе при этом закроет Z-отчёт.',
      'open',
      true,
    );
  }

  if (shift === null) {
    return state(
      'shift_closed',
      'warning',
      'Смена не открыта',
      'Чтобы принимать оплату, откройте смену.',
      'open',
      true,
    );
  }

  if (shift.status === ShiftStatus.Opening) {
    return state(
      'shift_opening',
      'warning',
      'Открытие смены не завершено',
      'Продолжите открытие на этой кассе или отмените его.',
      'continue_opening',
      true,
    );
  }

  if (shift.status === ShiftStatus.Closing) {
    return state(
      'shift_closing',
      'warning',
      'Закрытие не завершено',
      'Продолжите закрытие: Морс проверит кассу и напечатает Z-отчёт, только если смена на кассе ещё открыта.',
      'continue_closing',
      true,
    );
  }

  if (shift.status !== ShiftStatus.Open) {
    return state('shift_closed', 'neutral', 'Смена закрыта', 'Откройте новую смену.', 'open', true);
  }

  const expiryState = serverExpiryState(shift, now);

  if (expiryState === 'blocked') {
    return state(
      'shift_expired',
      'danger',
      'Смена просрочена · оплата недоступна',
      'Смена идёт больше суток. Закройте её и откройте новую.',
      'close',
      true,
    );
  }

  if (operationalStatus.paused) {
    return state(
      'paused',
      'warning',
      'Чеки временно не печатаются',
      'Принимайте оплату как обычно, чек пробейте на самой кассе.',
    );
  }

  // Привязка рабочего места есть только у клубов с кассой: без фискализации «не подключена» — норма, а не поломка.
  if (
    operationalStatus.fiscalization_enabled &&
    operationalStatus.workstation_binding === OrderFiscalWorkstationBinding.NotBound
  ) {
    return state(
      'workstation_not_bound',
      'danger',
      'Касса не подключена к клубу',
      'Касса не подключена к клубу. Свяжитесь с управляющим.',
      null,
      true,
    );
  }

  if (operationalStatus.fiscalization_enabled && handshake?.kind === 'bridge_missing') {
    return state(
      'bridge_missing',
      'neutral',
      'Кассы на этом устройстве нет',
      'Оплату с чеком принимайте на компьютере, к которому подключена касса.',
      null,
      true,
    );
  }

  if (operationalStatus.fiscalization_enabled && handshake !== null && handshake.kind !== 'ready') {
    return state('unavailable', 'danger', 'Касса не отвечает', handshake.instruction, null, true);
  }

  if (operationalStatus.fiscalization_enabled && handshake?.kind === 'ready') {
    const kktBlocker = fiscalKktBlocker(handshake.kkt, now, zone);
    if (kktBlocker !== null) return kktBlocker;

    if (
      operationalStatus.expected_kkt_device_id !== null &&
      handshake.kktDevice.idDevice !== operationalStatus.expected_kkt_device_id
    ) {
      return state(
        'device_mismatch',
        'danger',
        'Подключена другая касса',
        'К этому компьютеру подключена другая касса. Свяжитесь с управляющим.',
        null,
        true,
      );
    }

    if (!operationalStatus.cashier_display_name?.trim()) {
      return state(
        'cashier_missing',
        'danger',
        'Не заполнено, кто печатается в чеке',
        'Попросите управляющего указать должность и фамилию в настройках кассы.',
        null,
        true,
      );
    }

    if (handshake.kkt.sessionState === 1) {
      return state(
        'shift_kkt_desync',
        'danger',
        'Смена на кассе закрыта извне',
        'Закройте смену в Морсе, Z-отчёт печатать не нужно. Или продолжите работу: на кассе откроется новая смена.',
        'close',
        true,
      );
    }

    if (handshake.kkt.sessionState === 3) {
      return state(
        'shift_expired',
        'danger',
        'Смена просрочена · оплата недоступна',
        'Смена идёт больше суток. Закройте её и откройте новую.',
        'close',
        true,
      );
    }

    if (handshake.kkt.sessionState !== 2) {
      return state(
        'shift_unknown',
        'danger',
        'Не удалось проверить смену',
        'Касса ответила непонятно. Закройте смену: если касса её не закроет, смену можно закрыть аварийно.',
        'close',
        true,
      );
    }

    const kktWarning = fiscalKktWarning(handshake.kkt);

    if (kktWarning !== null) {
      return kktWarning;
    }
  }

  if (expiryState === 'warning') {
    const hours = Math.max(
      20,
      Math.floor((now.epochMilliseconds - (parseInstant(shift.opened_at)?.epochMilliseconds ?? NaN)) / 3_600_000),
    );
    return state(
      'shift_expiring',
      'warning',
      `Смена идёт ${hours} ч · закройте до ${formatTime(shift.expires_at, zone)}`,
      `Открыта в ${formatTime(shift.opened_at, zone)}. До блокировки оплаты осталось ${formatRemaining(shift.expires_at, now)}.`,
      'close',
    );
  }

  const operator = operatorSurname(shift.opened_by?.title);
  return state(
    'shift_open',
    'success',
    `Смена идёт ${formatElapsed(shift.opened_at, now)}${operator === null ? '' : ` · ${operator}`}`,
    `Открыта в ${formatTime(shift.opened_at, zone)}. Можно принимать оплату.`,
    'close',
  );
}

/**
 * Непреграждающий диагноз ККТ — брат `fiscalKktBlocker`. Ошибка обмена с ОФД не мешает ФН
 * подписывать чеки: накопленные документы уходят при восстановлении связи. Единая политика
 * проекта — предупреждать, не блокировать; см. `docs/fiscalization.md` и
 * `.ai-factory/references/kkmserver-browser-extension.md`.
 */
export function fiscalKktWarning(info: KkmKktInfo): FiscalShiftState | null {
  return info.ofdError === null
    ? null
    : state(
        'ofd_error',
        'warning',
        'Касса не отправляет чеки в налоговую',
        'Работать можно, но передайте управляющему — нужно проверить интернет на кассе.',
      );
}

export function fiscalKktBlocker(
  info: KkmKktInfo,
  now: Temporal.Instant = clock.now(),
  timezone?: string,
): FiscalShiftState | null {
  if (info.paperOver === true) {
    return state(
      'paper_over',
      'danger',
      'Кончилась бумага',
      'Вставьте новый рулон — как только касса ответит, значок станет зелёным.',
      null,
      true,
    );
  }

  if (info.fnMemoryOverflow === true) {
    return state(
      'fn_memory_overflow',
      'danger',
      'Память кассы заполнена',
      'Принимать оплату с чеком нельзя — свяжитесь с управляющим.',
      null,
      true,
    );
  }

  if (isExpired(info.fnDateEnd, now, timezone)) {
    return state(
      'fn_expired',
      'danger',
      'У кассы истёк срок памяти для чеков',
      'Принимать оплату с чеком нельзя — свяжитесь с управляющим.',
      null,
      true,
    );
  }

  if (info.fiscal !== true || info.fnNumber === null || info.inn === null || info.kktNumber === null) {
    return state(
      'kkt_not_ready',
      'danger',
      'Касса не готова к работе',
      'Принимать оплату с чеком нельзя — свяжитесь с управляющим.',
      null,
      true,
    );
  }

  return null;
}

/** Смена ККТ живёт (2 — открыта, 3 — открыта и просрочена) и требует Z-отчёта перед новой. */
function isKktSessionOpen(operationalStatus: FiscalOperationalStatus, handshake: KkmHandshakeResult | null): boolean {
  return (
    operationalStatus.fiscalization_enabled &&
    handshake?.kind === 'ready' &&
    (handshake.kkt.sessionState === 2 || handshake.kkt.sessionState === 3)
  );
}

function isExpired(value: string | null, now: Temporal.Instant, timezone?: string): boolean {
  const expiresAt = parseKkmDateTime(value, timezone);
  return expiresAt !== null && Temporal.Instant.compare(expiresAt, now) < 0;
}

function serverExpiryState(shift: ShiftFieldsFragment, now: Temporal.Instant): 'blocked' | 'normal' | 'warning' {
  const expiresAt = parseInstant(shift.expires_at);
  if (!expiresAt) return 'blocked';
  const remainingMinutes = now.until(expiresAt).total('minutes');
  if (remainingMinutes <= 10) return 'blocked';
  return remainingMinutes <= 240 ? 'warning' : 'normal';
}

function formatTime(value: string, zone: string | undefined): string {
  const dateTime = inTimezone(value, zone);
  return dateTime ? formatLocalTime(dateTime) : '—:—';
}

function formatRemaining(expiresAt: string, now: Temporal.Instant): string {
  const end = parseInstant(expiresAt);
  return end ? formatMinutes(Math.max(0, Math.ceil(now.until(end).total('minutes')))) : '—';
}

/** Сколько смена уже идёт: чип обновляет её каждые полминуты вместе с now. */
function formatElapsed(openedAt: string, now: Temporal.Instant): string {
  const start = parseInstant(openedAt);
  return start ? formatMinutes(Math.max(0, Math.floor(start.until(now).total('minutes')))) : '—';
}

export function formatMinutes(totalMinutes: number): string {
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;

  return hours > 0 ? `${hours} ч ${minutes} мин` : `${minutes} мин`;
}

function operatorSurname(title: string | null | undefined): string | null {
  const words = title?.trim().split(/\s+/).filter(Boolean) ?? [];

  return words.at(-1) ?? null;
}

function state(
  status: FiscalShiftStatus,
  tone: FiscalShiftTone,
  title: string,
  description: string,
  action: FiscalShiftAction = null,
  blocking = false,
): FiscalShiftState {
  return { action, blocking, description, kktStatePending: false, status, title, tone };
}
